/**
 * Adding the app to the Home Screen, which on iOS is a prerequisite rather than
 * a flourish.
 *
 * Safari refuses the Push API to an ordinary tab. The same page, added to the
 * Home Screen from the Share sheet, gets notifications on the Lock Screen and
 * in Notification Center. So on the one platform where push is the only
 * mechanism that works at all (§3.1 of `docs/push-notifications.md`: iOS has no
 * background socket and no substitute), installing *is* the feature. That is
 * why this exists and why it is not a growth prompt.
 *
 * # Two platforms, two mechanisms, and only one of them is a button
 *
 * Chrome, Edge and Chrome on Android fire `beforeinstallprompt`, which can be
 * stashed and replayed from a click, so those get a real button that installs.
 * Safari fires nothing and has no API: there is no way to install from script
 * on iOS, at all, and a button that could only ever describe itself would be a
 * lie. So iOS gets a sentence naming the two taps.
 *
 * # It asks once
 *
 * Dismissal is permanent, in `localStorage`. An install prompt that returns is
 * indistinguishable from adware, and the Settings screen keeps a way in for
 * anyone who changes their mind -- which is the right place for a decision
 * someone has to go looking for, rather than one shoved in front of them.
 */

import { signal } from "@preact/signals";

import { isPublicSession } from "./publicSession";

/**
 * The stashed `beforeinstallprompt`, when a browser has offered one.
 *
 * Typed here because TypeScript's DOM library still has no definition for it:
 * it is a Chromium extension to the platform rather than a specified event, and
 * `skipLibCheck` will not invent one.
 */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: InstallPromptEvent | null = null;

/** True once a browser has told us it would install this, and not since done so. */
export const installable = signal(false);

/** True when the page is already running as an installed app. */
export const installed = signal(isStandalone());

/** Set by the user, and never unset by anything here. */
export const dismissed = signal(readDismissed());

/**
 * Whether iOS is the platform, and therefore whether the hint is instructions.
 *
 * User-agent sniffing, which is normally the wrong tool -- and is the only tool
 * here. There is no feature to detect: the thing being detected is the *absence*
 * of an API that no other platform lacks, and "no `beforeinstallprompt` yet"
 * is also what a Chrome that has not decided yet looks like.
 *
 * iPadOS reports itself as a Mac, so the touch-point test is what separates an
 * iPad from a desktop Safari that has no Home Screen to add to.
 */
export const isIOS =
  /iPhone|iPod/u.test(navigator.userAgent) ||
  (/Macintosh/u.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

/** Whether the hint should be on screen at all. */
export function shouldOfferInstall(): boolean {
  if (installed.value || dismissed.value) return false;
  return installable.value || isIOS;
}

export function watchInstall(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Kept from firing Chrome's own mini-infobar, so the app decides when and
    // where this appears rather than the browser deciding for it.
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    installable.value = true;
  });

  window.addEventListener("appinstalled", () => {
    deferred = null;
    installable.value = false;
    installed.value = true;
  });

  // Launched from the Home Screen icon rather than a tab, or moved into one
  // mid-session, which Chrome does without reloading.
  window.matchMedia("(display-mode: standalone)").addEventListener("change", () => {
    installed.value = isStandalone();
  });
}

/**
 * Show the browser's install dialogue. Returns whether it went ahead.
 *
 * The stashed event is single-use -- a second `prompt()` on the same object
 * throws -- so it is dropped either way, and a browser that wants to offer
 * again will fire a fresh one.
 */
export async function promptInstall(): Promise<boolean> {
  const event = deferred;
  if (!event) return false;
  deferred = null;
  installable.value = false;
  try {
    await event.prompt();
    const choice = await event.userChoice;
    return choice.outcome === "accepted";
  } catch {
    return false;
  }
}

export function dismiss(): void {
  dismissed.value = true;
  // The signal is what hides the hint, so a public session gets the dismissal
  // it asked for and the machine gets no record of it. Gated on the write only:
  // unlike the other keys here this one is a nag counter rather than anything
  // about the person, so inheriting the previous user's "no thanks" is a
  // courtesy rather than a leak.
  if (isPublicSession()) return;
  try {
    localStorage.setItem(DISMISSED_KEY, "1");
  } catch {
    // Private browsing throws. The hint returns next session, which is the
    // least bad way to be wrong about a preference that could not be stored.
  }
}

const DISMISSED_KEY = "hm.install.dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * `navigator.standalone` is Safari's own, and it is the only signal iOS gives:
 * `display-mode: standalone` did not match in Home Screen web apps for years,
 * and a client that trusted only the media query would offer to install an app
 * that was already installed.
 */
function isStandalone(): boolean {
  const legacy = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return legacy || window.matchMedia("(display-mode: standalone)").matches;
}
